import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Mic, Square, Play, Pause, Loader2, Sparkles, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TaskCompletionDialog = ({ open, onOpenChange, task, onSuccess }) => {
  const { currentUser } = useUser();
  const [notes, setNotes] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioURL, setAudioURL] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [summary, setSummary] = useState('');

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setNotes('');
      setAudioBlob(null);
      setAudioURL('');
      setIsPlaying(false);
      setRecordingTime(0);
      setTranscript('');
      setSummary('');
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [open]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioURL(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('No se pudo acceder al micrófono');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleComplete = async () => {
    if (!notes.trim() && !audioBlob) {
      toast.error('Por favor, añade notas o graba un audio explicando cómo se completó la tarea');
      return;
    }

    setLoading(true);
    try {
      const userEmail = currentUser?.email || 'user@example.com';
      const headers = { 'X-User-Email': userEmail };

      let audioBase64 = null;
      if (audioBlob) {
        audioBase64 = await blobToBase64(audioBlob);
      }

      // Complete the task
      await axios.post(
        `${API}/tasks/${task.id}/complete`,
        {
          completion_notes: notes || null,
          audio_url: audioBase64,
          completed_by: currentUser?.name || 'Usuario'
        },
        { headers }
      );

      toast.success('Tarea completada');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Error al completar la tarea');
    } finally {
      setLoading(false);
    }
  };

  const handleTranscribe = async () => {
    if (!audioBlob) {
      toast.error('No hay audio para transcribir');
      return;
    }

    setTranscribing(true);
    try {
      const userEmail = currentUser?.email || 'user@example.com';
      const headers = { 'X-User-Email': userEmail };

      // First save the task with audio
      const audioBase64 = await blobToBase64(audioBlob);
      await axios.post(
        `${API}/tasks/${task.id}/complete`,
        {
          completion_notes: notes || null,
          audio_url: audioBase64,
          completed_by: currentUser?.name || 'Usuario'
        },
        { headers }
      );

      // Then transcribe
      const response = await axios.post(
        `${API}/tasks/${task.id}/transcribe`,
        {},
        { headers }
      );

      setTranscript(response.data.transcript);
      toast.success('Audio transcrito correctamente');
    } catch (error) {
      console.error('Error transcribing:', error);
      toast.error('Error al transcribir el audio');
    } finally {
      setTranscribing(false);
    }
  };

  const handleSummarize = async () => {
    if (!transcript && !notes.trim()) {
      toast.error('No hay contenido para resumir. Añade notas o transcribe el audio primero.');
      return;
    }

    setSummarizing(true);
    try {
      const userEmail = currentUser?.email || 'user@example.com';
      const headers = { 'X-User-Email': userEmail };

      // If we have notes but no transcript saved yet, save them first
      if (notes.trim() && !transcript) {
        const audioBase64 = audioBlob ? await blobToBase64(audioBlob) : null;
        await axios.post(
          `${API}/tasks/${task.id}/complete`,
          {
            completion_notes: notes,
            audio_url: audioBase64,
            completed_by: currentUser?.name || 'Usuario'
          },
          { headers }
        );
      }

      const response = await axios.post(
        `${API}/tasks/${task.id}/summarize`,
        {},
        { headers }
      );

      setSummary(response.data.summary);
      toast.success('Resumen generado correctamente');
    } catch (error) {
      console.error('Error summarizing:', error);
      toast.error('Error al generar el resumen');
    } finally {
      setSummarizing(false);
    }
  };

  const handleCompleteWithSummary = async () => {
    setLoading(true);
    try {
      const userEmail = currentUser?.email || 'user@example.com';
      const headers = { 'X-User-Email': userEmail };

      let audioBase64 = null;
      if (audioBlob) {
        audioBase64 = await blobToBase64(audioBlob);
      }

      // Save everything
      await axios.put(
        `${API}/tasks/${task.id}`,
        {
          ...task,
          status: 'Completed',
          completion_notes: notes || null,
          completion_audio_url: audioBase64,
          completion_transcript: transcript || null,
          completion_summary: summary || null,
          completed_at: new Date().toISOString(),
          completed_by: currentUser?.name || 'Usuario'
        },
        { headers }
      );

      toast.success('Tarea completada con registro guardado');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Error al completar la tarea');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Completar Tarea</DialogTitle>
          <DialogDescription>
            Registra cómo se completó la tarea "{task?.title}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Text Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Notas de completado
            </Label>
            <Textarea
              id="notes"
              placeholder="Describe cómo se completó la tarea..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="completion-notes-input"
            />
          </div>

          {/* Audio Recording */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Mic className="w-4 h-4" />
              Nota de voz (opcional)
            </Label>
            
            <div className="flex items-center gap-3">
              {!audioURL ? (
                <Button
                  type="button"
                  variant={isRecording ? 'destructive' : 'outline'}
                  size="sm"
                  onClick={isRecording ? stopRecording : startRecording}
                  data-testid="record-audio-btn"
                >
                  {isRecording ? (
                    <>
                      <Square className="w-4 h-4 mr-2" />
                      Detener ({formatTime(recordingTime)})
                    </>
                  ) : (
                    <>
                      <Mic className="w-4 h-4 mr-2" />
                      Grabar audio
                    </>
                  )}
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={togglePlayback}
                    data-testid="play-audio-btn"
                  >
                    {isPlaying ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {formatTime(recordingTime)} grabados
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setAudioBlob(null);
                      setAudioURL('');
                      setRecordingTime(0);
                      setTranscript('');
                    }}
                  >
                    Borrar
                  </Button>
                </div>
              )}
            </div>

            {audioURL && (
              <audio
                ref={audioRef}
                src={audioURL}
                onEnded={() => setIsPlaying(false)}
                className="hidden"
              />
            )}

            {/* Transcribe Button */}
            {audioURL && !transcript && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleTranscribe}
                disabled={transcribing}
                data-testid="transcribe-btn"
              >
                {transcribing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Transcribiendo...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Transcribir con IA
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Transcript */}
          {transcript && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">Transcripción</Label>
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                {transcript}
              </div>
            </div>
          )}

          {/* Summarize Button */}
          {(transcript || notes.trim()) && !summary && (
            <Button
              type="button"
              variant="secondary"
              onClick={handleSummarize}
              disabled={summarizing}
              className="w-full"
              data-testid="summarize-btn"
            >
              {summarizing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generando resumen...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generar resumen con IA
                </>
              )}
            </Button>
          )}

          {/* Summary */}
          {summary && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-green-600 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Resumen generado
              </Label>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm">
                {summary}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="cancel-completion-btn"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={summary ? handleCompleteWithSummary : handleComplete}
            disabled={loading || (!notes.trim() && !audioBlob)}
            data-testid="confirm-completion-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              'Completar Tarea'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskCompletionDialog;
